import { createResourceStore, type ResourceStore } from "@/lib/resource";
import type { FinanceTransaction } from "./FinanceTransaction";
import { getCustomerFullName } from "./Customer";
import { apiClient } from "@/lib/http";

export interface FinanceDebt {
  id: string;

  // Polymorphic relations
  payable_type: string;
  payable_id: string;

  payer_type: string;
  payer_id: string;

  receiver_type: string;
  receiver_id: string;

  // Debt details
  amount: number;
  remaining: number;
  due_date?: string | null; // ISO date string (e.g. '2025-11-10')
  payment_date?: string | null; // ISO date string

  // Linked entities
  finance_transaction_id?: string | null;
  parentDebtId?: string | null;
  status: string;

  // Relationships (optional if eager loaded)
  transaction?: FinanceTransaction | null;
  parentDebt?: FinanceDebt | null;
  payable?: any | null;
  payer?: any | null;
  receiver?: any | null;

  // Timestamps
  created_at?: string;
  updated_at?: string;

  description: string;
}

interface CustomerDebtFormData {
  amount: number;
  due_date: Date | string;
  customer_id: string;
  shop_id: string;
}

interface SupplierCreditFormData {
  amount: number;
  due_date: Date | string;
  supplier_id: string;
  shop_id: string;
}

interface SupplierDebtFormData {
  amount: number;
  due_date: Date | string;
  supplier_id: string;
  shop_id: string;
}

interface FinanceDebtStore extends ResourceStore<FinanceDebt> {
  createCustomerDebt: (data: CustomerDebtFormData) => Promise<any>;
  createSupplierCredit: (data: SupplierCreditFormData) => Promise<any>;
  createSupplierDebt: (data: SupplierDebtFormData) => Promise<any>;
}

export const useFinanceDebt = createResourceStore<
  FinanceDebt,
  FinanceDebtStore
>("finance-debts", () => ({
  stats: {
    total_debt: 0,
    series: {
      labels: [
        "Jan 2025",
        "Feb 2025",
        "Mar 2025",
        "Apr 2025",
        "May 2025",
        "Jun 2025",
        "Jul 2025",
        "Aug 2025",
        "Sep 2025",
        "Oct 2025",
        "Nov 2025",
        "Dec 2025",
      ],
      datasets: [
        {
          label: "Customers Debts",
          data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 357960],
          color: "#3b82f6",
        },
        {
          label: "Suppliers Debts (Returns)",
          data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 275000],
          color: "#10b981",
        },
        {
          label: "Suppliers Credits (To Pay)",
          data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1218500],
          color: "#ef4444",
        },
      ],
    },
    top_customers_debitors: [],
    top_suppliers_debitors: [],
    top_suppliers_creditors: [],
  },

  createCustomerDebt: (data: CustomerDebtFormData) => {
    return apiClient().post("/finance-debts/customers", data);
  },
  createSupplierCredit: (data: SupplierCreditFormData) => {
    return apiClient().post("/finance-debts/suppliers/credits", data);
  },
  createSupplierDebt: (data: SupplierDebtFormData) => {
    return apiClient().post("/finance-debts/suppliers/debts", data);
  },
}));

export const getPayer = (debt: FinanceDebt) => {
  if (debt.payer_type.includes("Customer")) {
    return getCustomerFullName(debt.payer);
  }

  if (debt.payer_type.includes("Shop")) {
    return debt.payer?.name;
  }

  return debt.payer?.name || debt.payer_id || "—";
};

export const getReceiver = (debt: FinanceDebt) => {
  if (debt.receiver_type.includes("Customer")) {
    return getCustomerFullName(debt.receiver);
  }

  if (debt.receiver_type.includes("Shop")) {
    return debt.receiver?.name;
  }

  if (debt.receiver_type.includes("Supplier")) {
    return debt.receiver?.name;
  }
  return debt.receiver?.name || debt.receiver_id || "—";
};
